/**
 * SaveManager – persists game progress to localStorage so refreshing
 * the page resumes where the player left off.
 */

import type { Point } from '../types';

const STORAGE_KEY = '15fev-pixel-love-save';

export interface SaveData {
  version: 1;
  levelIndex: number;
  score: number;
  combo: number;
  phase: 'objectives' | 'exit' | 'complete';
  activeObjectiveIndex: number;
  playerPosition: Point;
  playerFacing: Point;
  activatedObjectiveIds: string[];
  /** Seconds already elapsed on the current level before this save. */
  levelElapsed: number;
}

/** Save current game progress to localStorage. */
export function saveProgress(data: SaveData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage full or unavailable – silently ignore
  }
}

/** Load saved progress. Returns null if nothing saved or data is invalid. */
export function loadProgress(): SaveData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SaveData;
    if (parsed.version !== 1) return null;
    if (typeof parsed.levelIndex !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Clear saved progress (e.g. when the game is fully completed). */
export function clearProgress(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
